import { get } from 'lodash-es';
import {
  DASH_DELIMITER,
  EMPTY_STRING,
  PATH_DELIMITER,
} from '@is24at/react-constants';
import { slugifyString } from '@is24at/react-utils';
import { ROUTE_EXPOSE_BASE_PATH } from 'utils/enums';

/**
 * CWIDs are case-sensitive Salesforce ids.
 * They consist of 19 chars including a '012.' prefix.
 */
// eslint-disable-next-line no-useless-escape
const CWID_PATTERN = '(012.[0-9a-zA-Z]{15})';

/**
 * Title slugs are lower case strings with alphanumeric chars, - and _
 */
const COMPANY_NAME_URL_PATTERN = '[0-9a-z-_]{1,227}';

/**
 * Expose related regex singletons.
 */
const CWID_REGEX = new RegExp(`^${CWID_PATTERN}$`);
const EXPOSE_PRETTY_TITLE_REGEX = new RegExp(
  `^(${COMPANY_NAME_URL_PATTERN})-${CWID_PATTERN}$`,
);

/**
 * Checks if the shape of {@code token} matches a cwid.
 */
export const couldBeCwid = (token: any) => CWID_REGEX.test(token);

/**
 * Parses the title fragment of an expose url and returns a result object or
 * null.
 *
 * It takes the :cwid or :titleSlug fragments of a path
 * and checks if it matches the title pattern. If yes, it returns
 * the cwid and if available the titleSlug.
 */
export const parseExposeUrlFragment = (token: any) => {
  if (couldBeCwid(token)) {
    return { cwid: token };
  }
  const matches = EXPOSE_PRETTY_TITLE_REGEX.exec(token);
  return (
    matches && {
      titleSlug: matches[1],
      cwid: matches[2],
    }
  );
};

/**
 * Parses and returns the cwid from location params or returns {@code null}.
 */
export const parseCwidFromUrlParams = ({ cwid, titleSlug }: any) => {
  const token = cwid || titleSlug || EMPTY_STRING;
  const parsed = parseExposeUrlFragment(token);
  return get(parsed, 'cwid', null);
};

/**
 * Builds and returns a pretty url for the given account.
 */
export const buildPrettyUrlFromAccount = ({ companyName, cwid }: any) => {
  const titleSlug = [slugifyString(companyName, 227), cwid].join(
    DASH_DELIMITER,
  );
  return `${ROUTE_EXPOSE_BASE_PATH}/${titleSlug}/`;
};

/**
 * Returns flag indicating whether user is on an expose page with a pretty url.
 */
export const isPrettyExposeRoute = (params: any) =>
  typeof params.titleSlug === 'string';

/**
 * Returns flag indicating whether the pretty url matches the account data.
 *
 * This function checks whether the matched path tokens in {@code params} are
 * valid for the data in {@code account}. A common use case for a mismatch is
 * when a realtor changed the company name and a user/bot requests the
 * page by the old pretty url.
 */
export const isInvalidPrettyExposeUrl = (params: any, account: any) => {
  const expected = buildPrettyUrlFromAccount(account);
  const activePrettyUrl = `${ROUTE_EXPOSE_BASE_PATH}/${params.titleSlug}/`;
  return expected !== activePrettyUrl;
};

/**
 * Returns flag indicating whether a url has trailing slash or not.
 */
export const urlHasTrailingSlash = (url: any) =>
  url && url.slice(-1) === PATH_DELIMITER;
